import React, { useState } from "react";
import { Tag as TagIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TAGS_MAX_COUNT, TAG_MAX_LENGTH, normalizeTag, normalizeTags, splitTagInput, tagProblem } from "../hqCustomerRules";

const PROBLEM_KEYS = {
  empty: "hc.tagEmpty", invisible: "hc.tagInvisible", tooLong: "hc.tagTooLong", control: "hc.tagControl", tooMany: "hc.tagTooMany",
};

/**
 * Add `parts` (raw texts) to `tags` with the server's rules (hqCustomerRules.js). Returns the new
 * list and the parts it refused, each with its reason, in order. Empty parts are dropped silently:
 * `a,,b` is two tags. A part with characters none of which renders (U+3164) is refused as invisible.
 */
function addParts(tags, parts) {
  let next = tags;
  const refused = [];
  parts.forEach((raw) => {
    const tag = normalizeTag(raw);
    if (!tag) return;
    const problem = tagProblem(tag);
    if (problem) { refused.push({ text: raw.trim(), problem }); return; }
    const grown = normalizeTags([...next, tag]);
    if (grown.length > TAGS_MAX_COUNT) { refused.push({ text: raw.trim(), problem: "tooMany" }); return; }
    next = grown;
  });
  return { next, refused };
}

/**
 * Chips with a remove control plus an add input. A comma ends a tag as it is typed; Enter, a paste
 * and leaving the field commit the whole text, split on commas (`a,b,c` is three tags), so a
 * half-typed tag is not lost to the Save click. A part the rules refuse stays in the field with
 * the reason, for the operator to fix; the valid parts around it are added.
 * Read-only (`onChange` omitted) renders the chips alone.
 *
 * `tags`: string[] (already normalised); `onChange(next: string[])`; `t`: the superadmin
 * translator; `inputId`: the id a visible <label> points at.
 */
export default function TagEditor({ tags, onChange, t, inputId }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const commit = (text, { commitRest }) => {
    const { parts, rest } = splitTagInput(text, { commitRest });
    const { next, refused } = addParts(tags, parts);
    if (next.length !== tags.length) onChange(next);
    setDraft([...refused.map((r) => r.text), rest].filter(Boolean).join(","));
    const first = refused[0];
    setError(first ? t(PROBLEM_KEYS[first.problem], { max: first.problem === "tooMany" ? TAGS_MAX_COUNT : TAG_MAX_LENGTH }) : "");
  };

  const onInput = (value) => {
    if (!value.includes(",")) { setDraft(value); setError(""); return; }
    commit(value, { commitRest: false });
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    commit(draft, { commitRest: true });
  };

  // A pasted list is committed as a whole; a pasted single word is ordinary typing.
  const onPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    if (!pasted.includes(",")) return;
    e.preventDefault();
    const { selectionStart, selectionEnd } = e.currentTarget;
    const start = selectionStart ?? draft.length;
    const end = selectionEnd ?? draft.length;
    commit(`${draft.slice(0, start)}${pasted}${draft.slice(end)}`, { commitRest: true });
  };

  return (
    <div data-testid="tag-editor">
      <div className="flex items-center gap-2 flex-wrap">
        {tags.map((tag) => (
          <span key={tag} data-testid={`tag-chip-${tag}`}
            className="text-xs inline-flex items-center gap-1 bg-white shadow-sm border border-slate-200 rounded-full px-2.5 py-0.5">
            <TagIcon className="w-3 h-3 text-amber-600" /> {tag}
            {onChange && (
              <button type="button" onClick={() => onChange(tags.filter((x) => x !== tag))}
                aria-label={t("hc.tagRemove", { tag })} data-testid={`tag-remove-${tag}`}>
                <X className="w-3 h-3 text-slate-400 hover:text-slate-800" />
              </button>
            )}
          </span>
        ))}
        {onChange && (
          // Fluid: the placeholder's length differs by language, so the field takes the room it has.
          <Input id={inputId} value={draft} placeholder={t("hc.tagsPh")} onChange={(e) => onInput(e.target.value)}
            onKeyDown={onKeyDown} onPaste={onPaste} onBlur={() => commit(draft, { commitRest: true })}
            aria-invalid={!!error}
            className="bg-white border-slate-200 h-7 text-xs flex-1 basis-80 min-w-0" data-testid="tag-input" />
        )}
      </div>
      {error && <div className="text-xs text-rose-600 mt-1" role="alert" data-testid="tag-error">{error}</div>}
    </div>
  );
}
